import { useEffect, useState } from 'react';

export type PollingResource<T> = {
  data?: T;
  error?: Error;
  loading: boolean;
};

export function usePollingResource<T>(
  load: () => Promise<T>,
  intervalMilliseconds = 10000,
): PollingResource<T> {
  const [resource, setResource] = useState<PollingResource<T>>({
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async (): Promise<void> => {
      try {
        const data = await load();
        if (!cancelled) {
          setResource({ data, loading: false });
        }
      } catch (caught) {
        if (!cancelled) {
          setResource(previous => ({
            data: previous.data,
            error: caught instanceof Error ? caught : new Error(String(caught)),
            loading: false,
          }));
        }
      }
      if (!cancelled) {
        timer = setTimeout(refresh, intervalMilliseconds);
      }
    };
    void refresh();
    return (): void => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [intervalMilliseconds, load]);

  return resource;
}
